import { isAuthorisedGoogleTagManagerUrl } from "../../../common/scriptSecurity";
import { loadScript } from "./scriptLoader";

/**
 * Tag types the container is not allowed to run, matching the `gtm.blocklist`
 * pushed by the legacy `dmn` pages. Arbitrary HTML, custom pixels and
 * non-Google scripts stay blocked so an embedder's container cannot inject
 * scripts into the checkout.
 */
export const GTM_BLOCKLIST = [
  "html",
  "customScripts",
  "customPixels",
  "nonGoogleScripts",
  "nonGoogleIframes",
  "nonGooglePixels",
  "sandboxedScripts",
] as const;

const injected = new Set<string>();
let blocklistPushed = false;

/**
 * Ensures `window.dataLayer` exists, whether or not an embedding page created it
 * first.
 */
export function ensureDataLayer(): Record<string, unknown>[] {
  window.dataLayer ??= [];

  return window.dataLayer;
}

/**
 * Pushes the tag blocklist, once per page, before any container we load.
 *
 * It is pushed only when we are about to load a container of our own, because the
 * blocklist configures whichever containers read the dataLayer: pushing it while
 * merely reporting an event would reconfigure a host page's own container, which
 * is theirs to configure and not ours — see `docs/analytics.md`.
 */
function pushBlocklist(dataLayer: Record<string, unknown>[]): void {
  if (blocklistPushed) return;

  blocklistPushed = true;
  dataLayer.push({ "gtm.blocklist": [...GTM_BLOCKLIST] });
}

/**
 * Injects a Google Tag Manager container.
 *
 * Safe to call repeatedly: a container is only ever injected once.
 *
 * @returns `true` when the container script loaded.
 */
export async function loadGtmContainer(containerId: string): Promise<boolean> {
  const dataLayer = ensureDataLayer();
  pushBlocklist(dataLayer);

  if (injected.has(containerId)) return true;
  injected.add(containerId);

  const src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
  if (!isAuthorisedGoogleTagManagerUrl(src)) return false;

  dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });

  return loadScript(src);
}

/** Pushes an event object onto the dataLayer, clearing any previous ecommerce object. */
export function pushToDataLayer(payload: Record<string, unknown>): void {
  const dataLayer = ensureDataLayer();
  dataLayer.push({ ecommerce: null });
  dataLayer.push(payload);
}

/** Clears injected container tracking. Test-only. */
export function resetGtmContainers(): void {
  injected.clear();
  blocklistPushed = false;
}
